import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { COUNTRIES } from "@/lib/countries";
import { useGeo } from "@/hooks/use-geo";
import { useUtm } from "@/hooks/use-utm";
import { sendWebengageCTAEvents } from "@/lib/webengage";

const B2B_FORM_ID = 19592;
const B2B_DEFAULT_PROFESSIONAL_TITLE = "Salaried Professional";
const B2B_WORK_EXPERIENCE_LABEL = "work_experience_range";
const B2B_DEFAULT_WORK_EXPERIENCE_TITLE = "1-2 years";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface QuestionOption {
  optionId: number;
  title: string;
}

interface Question {
  id: number;
  formsQuestionIdentityId: number;
  title: string;
  subText: string | null;
  placeholderText: string | null;
  questionCategory: string;
  isRequired: boolean;
  allowOthersOption: boolean | null;
  allowConditionality: boolean | null;
  minimumCharactersAllowed: string | null;
  maximumCharactersAllowed: string | null;
  label?: string;
  questionType: string;
  seqNumber: number;
  options?: QuestionOption[];
}

interface StandardQuestionFlow {
  optionId: number;
  followUpQuestions: Question[];
}

interface QuestionFlowResponse {
  scholarshipQuestions: Question[];
  exitQuestions: Question[];
  defaultStandardQuestion: Question;
  unconditionalQuestions: Question[];
  standardQuestionsFlow: StandardQuestionFlow[];
  is_whatsapp_disclaimer: boolean;
}

type DynamicAnswer =
  | { type: "text"; value: string }
  | { type: "textarea"; value: string }
  | { type: "choice"; optionId: number; title: string; otherText?: string };

function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

function isOthersTitle(title: string): boolean {
  return title.trim().toLowerCase() === "others";
}

function isAnswerValid(q: Question, a: DynamicAnswer | undefined): boolean {
  const opts = q.options ?? [];
  if (q.questionCategory === "TEXT" || q.questionCategory === "TEXT_AREA") {
    const v = a?.type === "text" || a?.type === "textarea" ? a.value.trim() : "";
    if (!q.isRequired) return true;
    return v.length > 0;
  }
  if (q.questionCategory === "DROPDOWN" || q.questionCategory === "ONE_OF_MANY") {
    if (!q.isRequired) {
      if (!a || a.type !== "choice" || a.optionId == null) return true;
    } else {
      if (!a || a.type !== "choice" || a.optionId == null) return false;
    }
    if (a.type !== "choice" || a.optionId == null) return true;
    const opt = opts.find((o) => o.optionId === a.optionId);
    if (!opt) return !q.isRequired;
    if (q.allowOthersOption && isOthersTitle(opt.title)) {
      if (!q.isRequired && !a.otherText?.trim()) return true;
      return Boolean(a.otherText?.trim());
    }
    return true;
  }
  return false;
}

function buildQuestionResponsePayload(
  q: Question,
  a: DynamicAnswer | undefined,
): Record<string, unknown> | null {
  if (!a) return null;
  const base = {
    questionId: q.id,
    formsQuestionIdentityId: q.formsQuestionIdentityId,
    questionType: q.questionType,
    questionCategory: q.questionCategory,
  };
  const opts = q.options ?? [];
  if (a.type === "text" || a.type === "textarea") {
    const v = a.value.trim();
    if (!v) return null;
    return { ...base, responses: [v] };
  }
  if (a.type === "choice") {
    const opt = opts.find((o) => o.optionId === a.optionId);
    if (!opt) return null;
    if (q.allowOthersOption && isOthersTitle(opt.title) && !a.otherText?.trim()) {
      return null;
    }
    const responseTitle =
      q.allowOthersOption && isOthersTitle(opt.title) && a.otherText?.trim()
        ? a.otherText.trim()
        : opt.title;
    return {
      ...base,
      optionIds: [a.optionId],
      responses: [responseTitle],
    };
  }
  return null;
}

function dynamicAnswerDisplayLine(
  q: Question,
  a: DynamicAnswer | undefined,
): string | null {
  if (!a) return null;
  if (a.type === "text" || a.type === "textarea") {
    const v = a.value.trim();
    if (!v) return null;
    return `${q.title}: ${v}`;
  }
  if (a.type === "choice") {
    const opts = q.options ?? [];
    const opt = opts.find((o) => o.optionId === a.optionId);
    if (!opt) return null;
    if (q.allowOthersOption && isOthersTitle(opt.title) && !a.otherText?.trim()) {
      return null;
    }
    const responseTitle =
      q.allowOthersOption && isOthersTitle(opt.title) && a.otherText?.trim()
        ? a.otherText.trim()
        : opt.title;
    return `${q.title}: ${responseTitle}`;
  }
  return null;
}

const inputClasses =
  "w-full bg-transparent border border-white/15 rounded-lg px-4 py-3.5 text-white text-sm placeholder:text-white/35 focus:outline-none focus:border-white/40 transition-colors";

const selectClasses =
  "w-full bg-transparent border border-white/15 rounded-lg px-4 py-3.5 text-white text-sm focus:outline-none focus:border-white/40 transition-colors appearance-none cursor-pointer";

function Label({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-sm font-semibold text-white"
    >
      {children}
      {required ? <span className="text-red-400">*</span> : null}
    </label>
  );
}

function ErrorText({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-400">{message}</p>;
}

function ChevronDown() {
  return (
    <svg
      className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/60"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export default function BusinessForm() {
  const geo = useGeo();
  const utm = useUtm();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [dialCountryCode, setDialCountryCode] = useState("IN");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [countryCode, setCountryCode] = useState("");

  const [experience, setExperience] = useState(B2B_DEFAULT_PROFESSIONAL_TITLE);
  const [experienceOptionId, setExperienceOptionId] = useState<number | null>(
    null,
  );

  const [questionFlow, setQuestionFlow] = useState<QuestionFlowResponse | null>(
    null,
  );
  const [dynamicAnswers, setDynamicAnswers] = useState<
    Record<number, DynamicAnswer>
  >({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const [dialCodeOpen, setDialCodeOpen] = useState(false);
  const dialBtnRef = useRef<HTMLButtonElement>(null);
  const dialRef = useRef<HTMLDivElement>(null);

  const sortedUnconditional = useMemo(() => {
    const list = [...(questionFlow?.unconditionalQuestions ?? [])];
    list.sort((a, b) => a.seqNumber - b.seqNumber);
    return list;
  }, [questionFlow]);

  const activeFollowUps = useMemo((): Question[] => {
    if (!questionFlow?.standardQuestionsFlow || experienceOptionId == null)
      return [];
    const flow = questionFlow.standardQuestionsFlow.find(
      (f) => f.optionId === experienceOptionId,
    );
    const list = [...(flow?.followUpQuestions ?? [])];
    list.sort((a, b) => a.seqNumber - b.seqNumber);
    return list;
  }, [questionFlow, experienceOptionId]);

  const visibleFollowUps = useMemo(
    () => activeFollowUps.filter((q) => q.label !== B2B_WORK_EXPERIENCE_LABEL),
    [activeFollowUps],
  );

  useEffect(() => {
    async function fetchQuestions() {
      try {
        const apiUrl = import.meta.env.VITE_FORM_API_URL;
        const res = await fetch(`${apiUrl}/forms/${B2B_FORM_ID}/questionflow`);
        if (res.ok) {
          const data = (await res.json()) as QuestionFlowResponse;
          setQuestionFlow(data);
        }
      } catch (e) {
        console.error("Error fetching question flow", e);
      }
    }
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (!geo.loaded) return;
    if (geo.error) {
      setCountry("India");
      setCountryCode("IN");
      return;
    }
    setCountry(geo.country);
    setCountryCode(geo.countryCode);
    if (geo.dialCode) {
      setDialCode(geo.dialCode);
      setDialCountryCode(geo.countryCode);
    }
  }, [geo.loaded, geo.error, geo.country, geo.countryCode, geo.dialCode]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dialRef.current &&
        !dialRef.current.contains(e.target as Node) &&
        dialBtnRef.current &&
        !dialBtnRef.current.contains(e.target as Node)
      ) {
        setDialCodeOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!dialCodeOpen) return;
    const el = dialRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
      if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
        e.preventDefault();
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [dialCodeOpen]);

  // Default professional_label = "Salaried Professional"
  useEffect(() => {
    const opts = questionFlow?.defaultStandardQuestion?.options;
    if (!opts?.length) {
      setExperience(B2B_DEFAULT_PROFESSIONAL_TITLE);
      setExperienceOptionId(null);
      return;
    }
    const opt =
      opts.find(
        (o) =>
          o.title.toLowerCase() === B2B_DEFAULT_PROFESSIONAL_TITLE.toLowerCase(),
      ) ?? opts[0];
    setExperience(opt.title);
    setExperienceOptionId(opt.optionId);
  }, [questionFlow]);

  // Auto-seed hidden work_experience_range default
  useEffect(() => {
    if (!questionFlow?.standardQuestionsFlow || experienceOptionId == null)
      return;
    const currentFlow = questionFlow.standardQuestionsFlow.find(
      (f) => f.optionId === experienceOptionId,
    );
    const wq = currentFlow?.followUpQuestions?.find(
      (q) => q.label === B2B_WORK_EXPERIENCE_LABEL,
    );
    const opts = wq?.options ?? [];
    const opt =
      opts.find(
        (o) =>
          o.title.toLowerCase() ===
          B2B_DEFAULT_WORK_EXPERIENCE_TITLE.toLowerCase(),
      ) ?? opts[0];
    if (!wq || !opt) return;
    setDynamicAnswers((prev) => ({
      ...prev,
      [wq.formsQuestionIdentityId]: {
        type: "choice",
        optionId: opt.optionId,
        title: opt.title,
      },
    }));
  }, [questionFlow, experienceOptionId]);

  // Form collapses to a short thank-you card; keep scrollY fixed otherwise the
  // viewport lands on Process/FAQs below. Only scroll up to the section if needed.
  useLayoutEffect(() => {
    if (!submitted) return;
    const el = sectionRef.current;
    if (!el) return;
    const headerOffset = 96;
    const targetTop =
      el.getBoundingClientRect().top + window.scrollY - headerOffset;
    if (window.scrollY > targetTop + 1) {
      window.scrollTo({ top: Math.max(0, targetTop), behavior: "instant" });
    }
  }, [submitted]);

  const selectedCountry = COUNTRIES.find((c) => c.code === dialCountryCode);
  const phoneMin = selectedCountry
    ? Array.isArray(selectedCountry.phoneLength)
      ? selectedCountry.phoneLength[0]
      : selectedCountry.phoneLength
    : 7;
  const phoneMax = selectedCountry
    ? Array.isArray(selectedCountry.phoneLength)
      ? selectedCountry.phoneLength[1]
      : selectedCountry.phoneLength
    : 15;



  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = "Name is required.";
    else if (name.trim().length < 2)
      errors.name = "Name must be at least 2 characters.";

    if (!email.trim()) errors.email = "Email is required.";
    else if (!EMAIL_RE.test(email.trim()))
      errors.email = "Please enter a valid email address.";

    if (!phone.trim()) errors.phone = "Phone number is required.";
    else if (phone.length < phoneMin || phone.length > phoneMax)
      errors.phone =
        phoneMin === phoneMax
          ? `Phone number must be ${phoneMin} digits.`
          : `Phone number must be ${phoneMin}–${phoneMax} digits.`;

    if (questionFlow) {
      for (const q of sortedUnconditional) {
        if (!isAnswerValid(q, dynamicAnswers[q.formsQuestionIdentityId])) {
          errors[`dyn-${q.formsQuestionIdentityId}`] = q.isRequired
            ? `${q.title} is required.`
            : `Please check ${q.title}.`;
        }
      }
      for (const q of activeFollowUps) {
        if (q.label === B2B_WORK_EXPERIENCE_LABEL) continue;
        if (!isAnswerValid(q, dynamicAnswers[q.formsQuestionIdentityId])) {
          errors[`dyn-${q.formsQuestionIdentityId}`] = q.isRequired
            ? `${q.title} is required.`
            : `Please check ${q.title}.`;
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function updateDynamicAnswer(
    identityId: number,
    next: DynamicAnswer | undefined,
  ) {
    setDynamicAnswers((prev) => {
      if (next === undefined) {
        const rest = { ...prev };
        delete rest[identityId];
        return rest;
      }
      return { ...prev, [identityId]: next };
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    if (!validate()) return;

    (document.activeElement as HTMLElement | null)?.blur();
    window.fbq?.("track", "Lead");

    setLoading(true);
    try {
      let channel = "youtube";
      if (utm.utmSource) {
        const s = utm.utmSource.toLowerCase();
        if (s.includes("instagram") || s === "ig") channel = "instagram";
        else if (s.includes("x") || s.includes("twitter")) channel = "x";
        else if (s.includes("youtube") || s === "yt") channel = "youtube";
      }

      const questionResponses: Array<Record<string, unknown>> = [];
      let experienceLabelForPayload = experience;

      if (questionFlow) {
        const expQuestion = questionFlow.defaultStandardQuestion;
        if (expQuestion && experience) {
          const expOpts = expQuestion.options ?? [];
          const expOptionId =
            experienceOptionId ??
            expOpts.find(
              (o) => o.title.toLowerCase() === experience.toLowerCase(),
            )?.optionId;
          const expOption =
            expOptionId != null
              ? expOpts.find((o) => o.optionId === expOptionId)
              : null;
          if (expOption) {
            experienceLabelForPayload = expOption.title;
            const isDefaultStandard =
              expQuestion.questionType === "DEFAULT_STANDARD_QUESTION";
            questionResponses.push({
              responses: [expOption.title],
              questionId: expQuestion.id,
              formsQuestionIdentityId: expQuestion.formsQuestionIdentityId,
              questionType: expQuestion.questionType,
              questionCategory: expQuestion.questionCategory,
              ...(isDefaultStandard ? {} : { optionIds: [expOption.optionId] }),
            });
          }
        }

        for (const q of sortedUnconditional) {
          const p = buildQuestionResponsePayload(
            q,
            dynamicAnswers[q.formsQuestionIdentityId],
          );
          if (p) questionResponses.push(p);
        }
        for (const q of activeFollowUps) {
          const p = buildQuestionResponsePayload(
            q,
            dynamicAnswers[q.formsQuestionIdentityId],
          );
          if (p) questionResponses.push(p);
        }
      }

      let yearMeta = "";
      if (questionFlow) {
        const wq = activeFollowUps.find(
          (q) => q.label === B2B_WORK_EXPERIENCE_LABEL,
        );
        if (wq) {
          const a = dynamicAnswers[wq.formsQuestionIdentityId];
          if (a?.type === "choice") {
            const opt = (wq.options ?? []).find(
              (o) => o.optionId === a.optionId,
            );
            yearMeta = opt?.title ?? "";
          }
        }
      }

      const dynamicFieldLines: string[] = [];
      for (const q of sortedUnconditional) {
        const line = dynamicAnswerDisplayLine(
          q,
          dynamicAnswers[q.formsQuestionIdentityId],
        );
        if (line) dynamicFieldLines.push(line);
      }
      for (const q of activeFollowUps) {
        const line = dynamicAnswerDisplayLine(
          q,
          dynamicAnswers[q.formsQuestionIdentityId],
        );
        if (line) dynamicFieldLines.push(line);
      }

      const apiUrl = import.meta.env.VITE_FORM_API_URL;
      await sendWebengageCTAEvents({
        form_id: B2B_FORM_ID,
        email: email.trim().toLowerCase(),
        name: name.trim(),
        phone: `${dialCode}${phone.trim()}`,
      });
      const payload = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: `${dialCode}${phone.trim()}`,
        channel,
        country: country || "India",
        countryCode: countryCode || "IN",
        dialCode,
        formId: B2B_FORM_ID,
        source: "stayingahead.club",
        subscribeNewsletter: true,
        enrollMastermind: true,
        dynamicFieldLines,
        utm_source: utm.utmSource || undefined,
        utm_medium: utm.utmMedium || undefined,
        utm_campaign: utm.utmCampaign || undefined,
        utm_content: utm.utmContent || undefined,
        utm_term: utm.utmTerm || undefined,
        utm_adset: utm.utmAdset || undefined,
        campaign_id: utm.campaignId
          ? parseInt(utm.campaignId) || undefined
          : undefined,
        questions: {
          experience: experienceLabelForPayload,
          yearOfExperience: yearMeta,
        },
        ...(questionResponses.length > 0 && { questionResponses }),
      };

      const response = await fetch(`${apiUrl}/api-forms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_FORMS_API_TOKEN}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || data.status === "error") {
        setError(data.message || "Something went wrong. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch (err) {
      console.error("Submission error", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Find a question in the API flow by label or title keyword.
  function findQ(keywords: string[]): Question | null {
    if (!questionFlow) return null;
    const all: Question[] = [
      ...sortedUnconditional,
      ...activeFollowUps,
    ];
    for (const q of all) {
      const lbl = (q.label || "").toLowerCase();
      const ttl = (q.title || "").toLowerCase();
      for (const kw of keywords) {
        const k = kw.toLowerCase();
        if (lbl === k || lbl.includes(k) || ttl.includes(k)) return q;
      }
    }
    return null;
  }

  const designationQ = useMemo(
    () => findQ(["designation", "job_title", "role"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionFlow, experienceOptionId],
  );
  const companyQ = useMemo(
    () => findQ(["company_name", "company name", "organization", "company"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionFlow, experienceOptionId],
  );
  const teamSizeQ = useMemo(
    () => findQ(["team_size", "team size", "company_size"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionFlow, experienceOptionId],
  );
  const programQ = useMemo(
    () => findQ(["program", "course", "interest"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionFlow, experienceOptionId],
  );

  const slotIds = useMemo(() => {
    const s = new Set<number>();
    for (const q of [designationQ, companyQ, teamSizeQ, programQ]) {
      if (q) s.add(q.formsQuestionIdentityId);
    }
    return s;
  }, [designationQ, companyQ, teamSizeQ, programQ]);

  const fallbackQuestions = useMemo((): Question[] => {
    const all: Question[] = [
      ...sortedUnconditional,
      ...visibleFollowUps,
    ];
    return all.filter((q) => !slotIds.has(q.formsQuestionIdentityId));
  }, [sortedUnconditional, visibleFollowUps, slotIds]);

  // Render a slot for a known text-type question.
  function renderTextSlot(
    q: Question | null,
    label: string,
    placeholder: string,
    forceRequired?: boolean,
  ) {
    if (!q) return null;
    const id = q.formsQuestionIdentityId;
    const val = dynamicAnswers[id];
    const err = fieldErrors[`dyn-${id}`];
    const required = forceRequired ?? q.isRequired;
    return (
      <div>
        <Label htmlFor={`bf-${id}`} required={required}>
          {label}
        </Label>
        <input
          id={`bf-${id}`}
          type="text"
          placeholder={placeholder}
          value={val?.type === "text" ? val.value : ""}
          onChange={(e) =>
            updateDynamicAnswer(id, { type: "text", value: e.target.value })
          }
          className={`${inputClasses} ${err ? "border-red-400/50" : ""}`}
          aria-invalid={!!err}
        />
        <ErrorText message={err} />
      </div>
    );
  }

  function renderDropdownSlot(
    q: Question | null,
    label: string,
    placeholder: string,
  ) {
    if (!q) return null;
    const id = q.formsQuestionIdentityId;
    const val = dynamicAnswers[id];
    const err = fieldErrors[`dyn-${id}`];
    const opts = q.options ?? [];
    const ch = val?.type === "choice" ? val : undefined;
    return (
      <div className="flex-1">
        <Label htmlFor={`bf-${id}`} required={q.isRequired}>
          {label}
        </Label>
        <div className="relative">
          <select
            id={`bf-${id}`}
            value={ch?.optionId ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (!raw) {
                updateDynamicAnswer(id, undefined);
                return;
              }
              const oid = Number(raw);
              const opt = opts.find((o) => o.optionId === oid);
              if (!opt) return;
              updateDynamicAnswer(id, {
                type: "choice",
                optionId: opt.optionId,
                title: opt.title,
                otherText:
                  q.allowOthersOption && isOthersTitle(opt.title) ? "" : undefined,
              });
            }}
            className={`${selectClasses} pr-10 ${err ? "border-red-400/50" : ""}`}
            aria-invalid={!!err}
          >
            <option value="" className="bg-black">
              {placeholder}
            </option>
            {opts.map((o) => (
              <option key={o.optionId} value={o.optionId} className="bg-black">
                {o.title}
              </option>
            ))}
          </select>
          <ChevronDown />
        </div>
        {ch && q.allowOthersOption && isOthersTitle(ch.title) ? (
          <input
            type="text"
            placeholder="Please specify"
            value={ch.otherText ?? ""}
            onChange={(e) =>
              updateDynamicAnswer(id, {
                type: "choice",
                optionId: ch.optionId,
                title: ch.title,
                otherText: e.target.value,
              })
            }
            className={`${inputClasses} mt-2`}
          />
        ) : null}
        <ErrorText message={err} />
      </div>
    );
  }

  // Generic dynamic question renderer for fallback questions.
  function renderApiQuestionField(q: Question) {
    const id = q.formsQuestionIdentityId;
    const val = dynamicAnswers[id];
    const err = fieldErrors[`dyn-${id}`];
    const opts = q.options ?? [];

    if (q.questionCategory === "TEXT") {
      return (
        <div key={id}>
          <Label htmlFor={`bf-${id}`} required={q.isRequired}>
            {q.title}
          </Label>
          <input
            id={`bf-${id}`}
            type="text"
            placeholder={q.placeholderText || ""}
            value={val?.type === "text" ? val.value : ""}
            onChange={(e) =>
              updateDynamicAnswer(id, { type: "text", value: e.target.value })
            }
            className={`${inputClasses} ${err ? "border-red-400/50" : ""}`}
            aria-invalid={!!err}
          />
          <ErrorText message={err} />
        </div>
      );
    }

    if (q.questionCategory === "TEXT_AREA") {
      return (
        <div key={id}>
          <Label htmlFor={`bf-${id}`} required={q.isRequired}>
            {q.title}
          </Label>
          <textarea
            id={`bf-${id}`}
            rows={4}
            placeholder={q.placeholderText || ""}
            value={val?.type === "textarea" ? val.value : ""}
            onChange={(e) =>
              updateDynamicAnswer(id, {
                type: "textarea",
                value: e.target.value,
              })
            }
            className={`${inputClasses} min-h-[96px] resize-y ${err ? "border-red-400/50" : ""}`}
            aria-invalid={!!err}
          />
          <ErrorText message={err} />
        </div>
      );
    }

    if (q.questionCategory === "DROPDOWN") {
      const ch = val?.type === "choice" ? val : undefined;
      return (
        <div key={id}>
          <Label htmlFor={`bf-${id}`} required={q.isRequired}>
            {q.title}
          </Label>
          <div className="relative">
            <select
              id={`bf-${id}`}
              value={ch?.optionId ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (!raw) {
                  updateDynamicAnswer(id, undefined);
                  return;
                }
                const oid = Number(raw);
                const opt = opts.find((o) => o.optionId === oid);
                if (!opt) return;
                updateDynamicAnswer(id, {
                  type: "choice",
                  optionId: opt.optionId,
                  title: opt.title,
                  otherText:
                    q.allowOthersOption && isOthersTitle(opt.title)
                      ? ""
                      : undefined,
                });
              }}
              className={`${selectClasses} pr-10 ${err ? "border-red-400/50" : ""}`}
              aria-invalid={!!err}
            >
              <option value="" className="bg-black">
                {q.isRequired ? "Select…" : "Optional — skip"}
              </option>
              {opts.map((o) => (
                <option
                  key={o.optionId}
                  value={o.optionId}
                  className="bg-black"
                >
                  {o.title}
                </option>
              ))}
            </select>
            <ChevronDown />
          </div>
          {ch && q.allowOthersOption && isOthersTitle(ch.title) ? (
            <input
              type="text"
              placeholder="Please specify"
              value={ch.otherText ?? ""}
              onChange={(e) =>
                updateDynamicAnswer(id, {
                  type: "choice",
                  optionId: ch.optionId,
                  title: ch.title,
                  otherText: e.target.value,
                })
              }
              className={`${inputClasses} mt-2`}
            />
          ) : null}
          <ErrorText message={err} />
        </div>
      );
    }

    if (q.questionCategory === "ONE_OF_MANY") {
      const ch = val?.type === "choice" ? val : undefined;
      return (
        <div key={id}>
          <span className="mb-2 block text-sm font-semibold text-white">
            {q.title}
            {q.isRequired ? <span className="text-red-400">*</span> : null}
          </span>
          <div
            role="radiogroup"
            aria-label={q.title}
            className="flex flex-col gap-2.5"
          >
            {opts.map((o) => (
              <label
                key={o.optionId}
                className="flex cursor-pointer items-start gap-2.5"
              >
                <span className="relative mt-0.5 h-4 w-4 shrink-0">
                  <input
                    type="radio"
                    name={`bf-radio-${id}`}
                    value={o.optionId}
                    checked={ch?.optionId === o.optionId}
                    onChange={() =>
                      updateDynamicAnswer(id, {
                        type: "choice",
                        optionId: o.optionId,
                        title: o.title,
                        otherText:
                          q.allowOthersOption && isOthersTitle(o.title)
                            ? (ch?.otherText ?? "")
                            : undefined,
                      })
                    }
                    className="absolute inset-0 h-4 w-4 cursor-pointer appearance-none rounded-full border border-white/30 bg-transparent"
                    style={{
                      backgroundColor:
                        ch?.optionId === o.optionId ? "white" : "transparent",
                      borderColor:
                        ch?.optionId === o.optionId
                          ? "white"
                          : "rgba(255,255,255,0.3)",
                    }}
                  />
                  {ch?.optionId === o.optionId ? (
                    <span className="pointer-events-none absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
                  ) : null}
                </span>
                <span className="text-sm text-white/90">{o.title}</span>
              </label>
            ))}
          </div>
          {ch && q.allowOthersOption && isOthersTitle(ch.title) ? (
            <input
              type="text"
              placeholder="Please specify"
              value={ch.otherText ?? ""}
              onChange={(e) =>
                updateDynamicAnswer(id, {
                  type: "choice",
                  optionId: ch.optionId,
                  title: ch.title,
                  otherText: e.target.value,
                })
              }
              className={`${inputClasses} mt-2`}
            />
          ) : null}
          <ErrorText message={err} />
        </div>
      );
    }

    return null;
  }

  if (submitted) {
    return (
      <section
        ref={sectionRef}
        id="resource-cta"
        className="w-full scroll-mt-24 bg-black font-fira text-white py-12 md:py-20 md:scroll-mt-28"
      >
        <div className="mx-auto w-[90%] md:w-[80%]">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center md:p-12">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
              <svg
                className="h-7 w-7"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-fira-condensed text-2xl font-medium text-white md:text-3xl">
              Thank you
            </h3>
            <p className="max-w-sm font-fira text-sm font-normal leading-relaxed text-[#FFFFFF85] md:text-base">
              We&apos;ve received your request. Our team will be in touch
              shortly.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="resource-cta"
      className="w-full scroll-mt-24 bg-black font-fira text-white py-12 md:py-20 md:scroll-mt-28"
    >
      {dialCodeOpen ? (
        <div
          className="fixed inset-0 z-[65] bg-black/40 sm:hidden"
          onClick={() => setDialCodeOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      <div className="mx-auto w-[90%] md:w-[80%]">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between md:gap-16">
          <div className="md:w-[40%]">
            <p className="font-fira text-sm font-normal uppercase tracking-[0.18em] text-[#FFFFFF]">
              Invest in growth
            </p>
            <h2 className="mt-3 font-fira-condensed text-[30px] font-medium leading-tight tracking-tight text-white md:mt-4 md:text-[42px] md:font-semibold">
              Ready to Start Your
              <br />
              Growth Journey?
            </h2>
          </div>

          <form
            onSubmit={handleSubmit}
            noValidate
            aria-busy={loading}
            className="flex w-full flex-col gap-5 md:w-[55%]"
          >
            <fieldset disabled={loading} className="flex flex-col gap-5">
              <div>
                <Label htmlFor="bf-name" required>
                  Full Name
                </Label>
                <input
                  id="bf-name"
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`${inputClasses} ${fieldErrors.name ? "border-red-400/50" : ""}`}
                  aria-invalid={!!fieldErrors.name}
                />
                <ErrorText message={fieldErrors.name} />
              </div>

              <div>
                <Label htmlFor="bf-email" required>
                  Work Email
                </Label>
                <input
                  id="bf-email"
                  type="email"
                  placeholder="Enter your email id"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${inputClasses} ${fieldErrors.email ? "border-red-400/50" : ""}`}
                  aria-invalid={!!fieldErrors.email}
                />
                <ErrorText message={fieldErrors.email} />
              </div>

              {renderTextSlot(designationQ, "Designation", "Enter your Designation")}
              {renderTextSlot(companyQ, "Company Name", "Enter your Company name", true)}

              <div>
                <Label required>Phone Number</Label>
                <div className="flex items-stretch gap-3">
                  <div className="relative shrink-0">
                    <button
                      ref={dialBtnRef}
                      type="button"
                      onClick={() => setDialCodeOpen(!dialCodeOpen)}
                      aria-expanded={dialCodeOpen}
                      aria-haspopup="listbox"
                      className="flex items-center gap-2 rounded-lg border border-white/15 bg-transparent px-3 py-3.5 text-white transition-colors hover:border-white/30"
                    >
                      <span className="text-base leading-none">
                        {countryFlag(dialCountryCode)}
                      </span>
                      <svg
                        className={`h-3.5 w-3.5 text-white/60 transition-transform ${dialCodeOpen ? "rotate-180" : ""}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {dialCodeOpen ? (
                      <div
                        ref={dialRef}
                        role="listbox"
                        aria-label="Country calling code options"
                        className="dropdown-scroll absolute left-0 top-full z-[80] mt-1 max-h-[min(15rem,40vh)] w-64 overflow-y-auto overscroll-contain rounded-lg border border-white/10 bg-black py-1 shadow-[0_8px_30px_rgba(0,0,0,0.5)] [touch-action:pan-y]"
                      >
                        {COUNTRIES.map((c) => (
                          <button
                            key={c.code}
                            type="button"
                            role="option"
                            aria-selected={dialCountryCode === c.code}
                            onClick={() => {
                              setDialCode(c.dialCode);
                              setDialCountryCode(c.code);
                              setDialCodeOpen(false);
                              const max = Array.isArray(c.phoneLength)
                                ? c.phoneLength[1]
                                : c.phoneLength;
                              if (phone.length > max) setPhone(phone.slice(0, max));
                            }}
                            className={`w-full cursor-pointer px-4 py-2.5 text-left text-sm transition-colors hover:bg-white hover:text-black ${
                              dialCountryCode === c.code
                                ? "text-white"
                                : "text-white/80"
                            }`}
                          >
                            <span className="mr-2">{countryFlag(c.code)}</span>
                            {c.name} ({c.dialCode})
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <input
                    id="bf-phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={phoneMax}
                    placeholder={`${dialCode} XXXXXXXXX`}
                    value={phone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "");
                      if (digits.length <= phoneMax) setPhone(digits);
                    }}
                    className={`${inputClasses} flex-1 ${fieldErrors.phone ? "border-red-400/50" : ""}`}
                    aria-invalid={!!fieldErrors.phone}
                  />
                </div>
                <ErrorText message={fieldErrors.phone} />
              </div>

              {(teamSizeQ || programQ) && (
                <div className="flex flex-col gap-5 md:flex-row md:gap-4">
                  {teamSizeQ ? (
                    <div className="md:w-[35%]">
                      {renderDropdownSlot(teamSizeQ, "Team Size", "Select")}
                    </div>
                  ) : null}
                  {programQ ? (
                    <div className="md:flex-1">
                      {renderDropdownSlot(
                        programQ,
                        "Program(s) you looking for",
                        "Select a program",
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {fallbackQuestions.length > 0 ? (
                <div className="flex flex-col gap-5">
                  {fallbackQuestions.map((q) => renderApiQuestionField(q))}
                </div>
              ) : null}
            </fieldset>

            {error ? (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-lg border border-red-400/20 bg-red-500/10 px-4 py-3 text-center text-sm text-red-400"
              >
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full rounded-lg bg-white px-6 py-3.5 font-fira text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 md:text-[0.9375rem]"
            >
              {loading ? "Submitting..." : "Request a Callback"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
